import { BuildVuPage, getManifest } from 'js/liswp/build_vu/api';
import React, { useMemo, useState, useEffect } from 'react';
import styled from 'astroturf/react';
import { Body } from 'js/design_system/Body';
import { ClaimCitation, RelatedClaim, Work, WorkClaim } from './types';
import { getClaimCitations } from './api';
import { createHighlights } from 'js/claims/buildVuClaimHighlights';
import ClaimsList from './ClaimsList';
import SourcePaperCard from './SourcePaperCard';
import SourceText from './SourceText';
import ClaimDetails from './ClaimDetails';
import RelatedPapers from './RelatedPapers';
import CitationModal from 'js/journals/CitationModal';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import trackClick from 'js/utils/trackClick';

type MobileStackOrderStyle = React.CSSProperties & {
  '--mobile-stack-order'?: string;
};

function mobileStackOrder(order: number): MobileStackOrderStyle {
  return { '--mobile-stack-order': String(order) };
}

export const SUPPORTING_CLAIMS_ELEMENT_ID = 'supporting-claims-list';
export const CONTRADICTING_CLAIMS_ELEMENT_ID = 'contradicting-claims-list';

type SingleClaimPageProps = {
  claim: WorkClaim;
  attachment_id: number | null;
  research_interests?: string[];
  quoted_evidence: string;
  source_paper: Work;
  supporting_claims: RelatedClaim[];
  contradicting_claims: RelatedClaim[];
  similar_claims: RelatedClaim[];
  similar_works: Work[];
};

const Container = styled.div`
  background-color: #f9f8f6;
  min-height: 100vh;
  padding: var(--spacing-md-48, 48px) var(--spacing-lg-64, 64px)
    var(--spacing-xlg-104, 104px);
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md-48, 48px);
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 767px) {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    padding: var(--spacing-md-48, 48px) 40px var(--spacing-xlg-104, 104px);
    gap: var(--spacing-md-48, 48px);
  }

  @media (max-width: 341px) {
    padding: var(--spacing-md-48, 48px) var(--spacing-sm-24, 24px)
      var(--spacing-xlg-104, 104px);
  }
`;

const LeftColumn = styled.div`
  flex: 1 1 320px;
  max-width: 745px;
  min-width: 0;

  @media (max-width: 767px) {
    display: contents;
  }
`;

const RightColumn = styled.div`
  flex: 1 1 280px;
  max-width: 519px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md-48, 48px);

  @media (max-width: 767px) {
    display: contents;
  }
`;

/** On desktop: invisible wrapper. On narrow screens: flex item with order for stack layout. */
const MobileStackSection = styled.div`
  display: contents;

  @media (max-width: 767px) {
    display: block;
    order: var(--mobile-stack-order);
    width: 100%;
    min-width: 0;
  }
`;

const RightRailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
`;

const Inner = styled.div`
  width: 100%;

  @media (max-width: 767px) {
    display: contents;
  }
`;

const SourcePaperRailCard = styled.div`
  background: var(--background-white, #fff);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--corner-radius-radius-lg, 16px);
  padding: var(--spacing-sm-32, 32px);
  box-sizing: border-box;
`;

const SingleClaimPage: React.FunctionComponent<SingleClaimPageProps> = ({
  claim,
  research_interests = [],
  quoted_evidence,
  source_paper,
  supporting_claims,
  contradicting_claims,
  similar_claims,
  similar_works,
  attachment_id,
}) => {
  const [manifestFetched, setManifestFetched] = useState(false);
  const [pages, setPages] = useState<BuildVuPage[]>([]);
  const [citationModalOpen, setCitationModalOpen] = useState(false);
  const [citations, setCitations] = useState<ClaimCitation[]>([]);

  const closeCitationsModal = () => {
    trackClick({
      source: 'single_claim_page.citation_modal.close',
      extra: {
        source_claim_id: claim.id,
      },
    });
    setCitationModalOpen(false);
  };

  const openCitationsModal = () => {
    trackClick({
      source: 'single_claim_page.citation_modal.open',
      extra: {
        source_claim_id: claim.id,
      },
    });
    setCitationModalOpen(true);
  };

  useEffect(() => {
    const loadCitations = async () => {
      try {
        const response = await getClaimCitations(claim.work_id);
        setCitations(response.citations);
      } catch (error) {
        console.error('Failed to load citations:', error);
      }
    };
    loadCitations();
  }, [claim.work_id]);

  useEffect(() => {
    if (manifestFetched || !attachment_id) return;
    getManifest(attachment_id)
      .then((manifest) => {
        const pageNumbers = claim.location_data.map(
          (location) => location.page,
        );
        setPages(
          manifest.filter((page) => pageNumbers.includes(page.page_number)),
        );
        setManifestFetched(true);
      })
      .catch(console.error);
  }, [attachment_id, manifestFetched, claim.location_data]);

  const sortedPages = useMemo(
    () => [...pages].sort((a, b) => a.page_number - b.page_number),
    [pages],
  );

  const hasRightColumn =
    sortedPages.length > 0 || Boolean(source_paper) || similar_works.length > 0;

  useEffect(() => {
    if (pages.length === 0) return;

    const highlightColor = 'rgba(204, 255, 212, .40)';
    claim.location_data.forEach((location) => {
      const pageDimensions = location.page_dimensions;
      const boundingBox = {
        page: location.page,
        xmin: location.x,
        ymin: location.y,
        width: location.width,
        height: location.height,
      };
      createHighlights([boundingBox], pageDimensions, highlightColor).catch(
        (error) => {
          console.error('Failed to create highlights:', error);
        },
      );
    });
  }, [pages, claim.location_data]);

  return (
    <Container>
      <LeftColumn>
        <Inner>
          <MobileStackSection style={mobileStackOrder(1)}>
            <ClaimDetails
              claim={claim}
              sourcePaper={source_paper}
              quotedEvidence={quoted_evidence}
              openCitationsModal={openCitationsModal}
              researchInterests={research_interests}
              supportingClaims={supporting_claims}
              contradictingClaims={contradicting_claims}
            />
          </MobileStackSection>
          {supporting_claims.length > 0 && (
            <MobileStackSection style={mobileStackOrder(4)}>
              <div id={SUPPORTING_CLAIMS_ELEMENT_ID}>
                <ImpressionWatcher
                  source="single_claim_page.supporting_claims"
                  extra={{
                    source_claim_id: claim.id,
                  }}
                >
                  <ClaimsList
                    sourceClaim={claim}
                    title="Supporting claims"
                    entries={supporting_claims}
                  />
                </ImpressionWatcher>
              </div>
            </MobileStackSection>
          )}
          {contradicting_claims.length > 0 && (
            <MobileStackSection style={mobileStackOrder(5)}>
              <div id={CONTRADICTING_CLAIMS_ELEMENT_ID}>
                <ImpressionWatcher
                  source="single_claim_page.contradicting_claims"
                  extra={{
                    source_claim_id: claim.id,
                  }}
                >
                  <ClaimsList
                    sourceClaim={claim}
                    title="Contradictory claims"
                    entries={contradicting_claims}
                  />
                </ImpressionWatcher>
              </div>
            </MobileStackSection>
          )}
          {similar_claims.length > 0 && (
            <MobileStackSection style={mobileStackOrder(6)}>
              <ImpressionWatcher
                source="single_claim_page.related_claims"
                extra={{
                  source_claim_id: claim.id,
                }}
              >
                <ClaimsList
                  sourceClaim={claim}
                  title="Related claims"
                  entries={similar_claims}
                />
              </ImpressionWatcher>
            </MobileStackSection>
          )}
        </Inner>
      </LeftColumn>

      {hasRightColumn && (
        <RightColumn>
          {source_paper && sortedPages.length > 0 && (
            <MobileStackSection style={mobileStackOrder(2)}>
              <SourceText
                sourceClaim={claim}
                sortedPages={sortedPages}
                sourcePaper={source_paper}
              />
            </MobileStackSection>
          )}
          {source_paper && (
            <MobileStackSection style={mobileStackOrder(3)}>
              <RightRailSection>
                <Body bold size="lg" as="h2">
                  Source paper
                </Body>
                <SourcePaperRailCard>
                  <SourcePaperCard
                    sourceClaim={claim}
                    paper={source_paper}
                    openCitationsModal={openCitationsModal}
                    thumbnailWidth={60}
                    thumbnailHeight={88}
                  />
                </SourcePaperRailCard>
              </RightRailSection>
            </MobileStackSection>
          )}
          {similar_works.length > 0 && (
            <MobileStackSection style={mobileStackOrder(7)}>
              <ImpressionWatcher
                source="single_claim_page.related_works"
                extra={{
                  source_claim_id: claim.id,
                }}
              >
                <RelatedPapers sourceClaim={claim} works={similar_works} />
              </ImpressionWatcher>
            </MobileStackSection>
          )}
        </RightColumn>
      )}
      <CitationModal
        open={citationModalOpen}
        citations={citations}
        onClose={closeCitationsModal}
        entity_id={claim.work_id}
        modalTitle="Cite this claim"
        source="single_claim_page"
      />
    </Container>
  );
};

export default SingleClaimPage;
